import { defineComponent as _defineComponent } from 'vue'
import { ObjectType, UpcomingRelease } from '@/@types/graphql-types';
import { BuyBoxOffers, useBuyBoxData } from '@/features/buybox/composables/useBuyboxData';
import {
	GetBuyBoxOffersDocument,
	GetBuyBoxOffersQueryVariables,
	type GetBuyBoxOffersQuery,
} from '@/features/buybox/graphql/queries/GetBuyBoxOffers.query';
// EXP_LINEAR_TV
import {
	GetLinearOfferEpisodesDocument,
	type GetLinearOfferEpisodesQuery,
	type GetLinearOfferEpisodesQueryVariables,
} from '@/features/buybox/graphql/queries/GetLinearOfferEpisodes.query';
// EXP_LINEAR_TV
import {
	GetExpandedBuyRentOffersDocument,
	type GetExpandedBuyRentOffersQuery,
	type GetExpandedBuyRentOffersQueryVariables,
} from '@/features/buybox/graphql/queries/GetExpandedBuyRentOffers.query';
import { buyboxFilterByMonetizationType } from '@/features/buybox/helpers/list-buybox-config';
import { useInternationalOffersStatus } from '@/features/international-offers/composables/useInternationalOffers';
import { useInternationalOffersTracking } from '@/features/international-offers/tracking';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import { useQuery } from '@/helpers/composables/useApollo';
import { mrcObservableOptions } from '@/helpers/composables/useBatchImpressionTracking';
import { useI18n } from '@/helpers/composables/useI18n';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { useTitleWatchlist } from '@/helpers/composables/useTitleWatchlist';
import { timestamp } from '@/helpers/date-helper';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { SnowplowTitleContextGraphql, type SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { ClickoutPlacement } from '@/interfaces/snowplow/clickout-context';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { BuyBoxFilterOptions, NewBuyBoxView } from '@/stores/useUserStoreTypes';
import type { EpisodeFragment } from '@/pages/graphql/fragments/Episode.fragment';
import { computed, provide, ref, watch } from 'vue';

// need to import this immediately because it's above the fold and could cause CLS
import BuyBoxCountrySwitcher from '@/features/buybox/components/BuyBoxCountrySwitcher.vue';
import BuyBoxTypeSwitcher from '@/features/buybox/components/BuyBoxTypeSwitcher.vue';
import ListBuyBoxFilter from '@/features/buybox/components/list/ListBuyBoxFilter.vue';
import ListUpcomingNoOffers from '@/features/buybox/components/list/ListUpcomingNoOffers.vue';
import PromotedListBuyBox from '@/features/buybox/components/list/PromotedListBuyBox.vue';
import RegularListBuyBox from '@/features/buybox/components/list/RegularListBuyBox.vue';
import { useActivateExperiment, useExperiment } from '@/libraries/experiment';
import { useTitleDetailInfo } from '@/pages/titleDetailV1/v1/composables/useTitleDetailInfo';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useTitleDetailsStore } from '@/stores/pinia/useTitleDetailsStore';

import { provideLocal } from '@vueuse/core';
import {
	useProviderBuyboxFilter,
	PROVIDER_EXP_BB_OFFER_PROVIDERS_KEY,
} from '@/components/experiments/ProviderBuyboxExp/composables/useProviderBuyboxFilter';

export default /*#__PURE__*/_defineComponent({
  __name: 'ListBuyBox',
  props: {
    title: null,
    episode: null,
    buyboxOffers: { default: undefined },
    sponsoredAd: { default: undefined },
    buyBoxSwitchType: null,
    hideBuyBoxSwitcher: { type: Boolean, default: false },
    additionalContexts: { default: () => [] },
    episodeNumber: null,
    placement: { default: 'regular_buybox' },
    upcomingEpisodeReleases: { default: null },
    hidePromotedBuyBox: { type: Boolean, default: false }
  },
  emits: {
	onWatchlistToggle: (createdAt: string | null) => ({ createdAt }),
},
  setup(__props: any, { emit: emits }) {

const props = __props as { title: TitleDetail, episode?: EpisodeFragment, buyboxOffers: BuyBoxOffers | null, sponsoredAd: SponsoredAdFragment | undefined, buyBoxSwitchType?: NewBuyBoxView, hideBuyBoxSwitcher: boolean, additionalContexts: SnowplowContext[], episodeNumber?: number | null, placement: ClickoutPlacement, upcomingEpisodeReleases: UpcomingRelease[] | null, hidePromotedBuyBox: boolean };

const OfferReporter = () => import('@/features/buybox/components/OfferReporter.vue');
const PlexPlayerModal = () => import('@/ui-components/video-player-modals/PlexPlayerModal.vue');
const BuyboxInternationalOffers = () =>
	import('@/features/international-offers/components/BuyboxInternationalOffers.vue');




const { setInWatchlist, onIsLoggedInChange } = useTitleWatchlist(props.sponsoredAd);

const userStore = useUserStore();
const { setNewBuyboxFilterType, setBuyboxFilterTypeOptimistic } = userStore;
const { newBuyboxFilterType, buyboxFilterTypeOptimistic, isLoggedIn } = storeToRefs(userStore);

const { country, language } = storeToRefs(useLanguageStore());
const { titleProviderCount } = storeToRefs(useConstantStore());
const { inLists } = storeToRefs(useTitleDetailsStore());

const { t } = useI18n();
const { trackInternationalOffers } = useInternationalOffersTracking({ title: () => props.title });

useProvideClickoutPlacement(props.placement);

const { titleId, titleName, titleObjectId, titleObjectType, isTitleUpcoming, seasonNumber, isInWatchList } =
	useTitleDetailInfo(() => props.title);

const { activeVariantWithControlGroup: mergeBuyRentExpVariant } = useExperiment('EXP_MERGE_BUY_RENT');

// EXP_LINEAR_TV
const { activeVariant: linearTVVariant } = useExperiment('EXP_LINEAR_TV');
const isLinearTvExperiment = computed(
	() => linearTVVariant.value === 'variant_1' || linearTVVariant.value === 'variant_2'
);
// EXP_LINEAR_TV

const buyBoxTitleId = computed(() => (props.episode ? props.episode.id : titleId.value));

const queryVariables = computed((): GetBuyBoxOffersQueryVariables => {
	return {
		// EXP_LINEAR_TV
		isLinearTvExperiment: isLinearTvExperiment.value,
		// EXP_LINEAR_TV
		nodeId: buyBoxTitleId.value ?? '',
		country: country.value,
		language: language.value,
		fallbackToForeignOffers: true,
	};
});

const { result, loading } = useQuery<GetBuyBoxOffersQuery, GetBuyBoxOffersQueryVariables>(
	GetBuyBoxOffersDocument,
	queryVariables,
	() => ({ enabled: !props.buyboxOffers })
);

// EXP_LINEAR_TV
const linearEpisodesQueryVariables = computed<GetLinearOfferEpisodesQueryVariables>(() => ({
	nodeId: buyBoxTitleId.value ?? '',
	country: country.value,
	language: language.value,
	fallbackToForeignOffers: true,
}));

const { result: linearEpisodesResult } = useQuery<GetLinearOfferEpisodesQuery, GetLinearOfferEpisodesQueryVariables>(
	GetLinearOfferEpisodesDocument,
	linearEpisodesQueryVariables,
	() => ({ enabled: !!props.buyboxOffers && isLinearTvExperiment.value })
);
// EXP_LINEAR_TV

// Bits and pieces for the expanded buy/rent offer query
const expandBuyRentOffers = computed(
	() =>
		newBuyboxFilterType.value === BuyBoxFilterOptions.BUY || newBuyboxFilterType.value === BuyBoxFilterOptions.RENT
);

const expandedQueryVariables = computed<GetExpandedBuyRentOffersQueryVariables>(() => ({
	nodeId: buyBoxTitleId.value ?? '',
	country: country.value,
	language: language.value,
	fallbackToForeignOffers: true,
}));

const { result: expandedResult } = useQuery<GetExpandedBuyRentOffersQuery, GetExpandedBuyRentOffersQueryVariables>(
	GetExpandedBuyRentOffersDocument,
	expandedQueryVariables,
	() => ({ enabled: expandBuyRentOffers.value })
);

const buyboxData = computed(() => {
	let baseOffers = !props.buyboxOffers ? (result.value?.node as BuyBoxOffers) : props.buyboxOffers;

	// EXP_LINEAR_TV: attach offerEpisodes (fetched separately above) onto the existing linear offers by id — this
	// only adds episode data, it never changes which offers are shown (see GetLinearOfferEpisodes.query.graphql).
	const linearEpisodesNode = linearEpisodesResult.value?.node;
	if (
		props.buyboxOffers &&
		isLinearTvExperiment.value &&
		linearEpisodesNode &&
		'linear' in linearEpisodesNode &&
		linearEpisodesNode.__typename === baseOffers.__typename
	) {
		const episodesByOfferId = new Map(linearEpisodesNode.linear.map(offer => [offer.id, offer.offerEpisodes]));
		baseOffers = {
			...baseOffers,
			linear: baseOffers.linear.map(offer => ({
				...offer,
				offerEpisodes: episodesByOfferId.get(offer.id) ?? offer.offerEpisodes,
			})),
		} as BuyBoxOffers;
	}

	// When the buy/rent filters are active, we fetch the expanded offers (on first usage) and merge client-side to display all of them
	const expandedNode = expandedResult.value?.node;
	if (
		!expandBuyRentOffers.value ||
		!expandedNode ||
		!('buy' in expandedNode) ||
		!('rent' in expandedNode) ||
		expandedNode.__typename !== baseOffers.__typename
	) {
		return baseOffers;
	}

	return {
		...baseOffers,
		buy: expandedNode.buy,
		rent: expandedNode.rent,
	} as BuyBoxOffers;
});

const isEpisodeBuybox = computed(() => buyboxData.value?.__typename === 'Episode');

// episode buybox filter is not currently being stored. keep state for each Episode buybox at buybox level
const episodeBuyBoxType = ref<BuyBoxFilterOptions>(BuyBoxFilterOptions.ALL);
const episodeBuyBoxTypeOptimistic = ref<BuyBoxFilterOptions>(BuyBoxFilterOptions.ALL);

const buyboxFilterOptimistic = computed(() => {
	if (isEpisodeBuybox.value) {
		return episodeBuyBoxType.value;
	}
	return buyboxFilterTypeOptimistic.value ?? newBuyboxFilterType.value;
});
const buyboxFilter = computed(() => (isEpisodeBuybox.value ? episodeBuyBoxType.value : newBuyboxFilterType.value));

const buyboxUpcomingReleases = computed(() => {
	return props.upcomingEpisodeReleases ?? props.title.content.upcomingReleases;
});

// EXP_PROVIDER_BUYBOX_V2
const { selectedBuyboxProviders, showSavePrompt, onSaveServicesSignUp } = useProviderBuyboxFilter({
	listFilterType: buyboxFilter,
});
const SaveServicesPrompt = () => import('@/components/experiments/ProviderBuyboxExp/components/SaveServicesPrompt.vue');
// EXP_PROVIDER_BUYBOX_V2

const {
	filteredRegularOffers,
	allRegularOffers,
	upcomingReleases,
	upcomingReleasesFromGroups,
	promotedOffers,
	AVODOffer,
	dvdBluRayOffers,
	hasVODOffers,
} = useBuyBoxData({
	title: () => props.title,
	buyboxData,
	buyboxFilter,
	buyboxUpcomingReleases,
	// EXP_PROVIDER_BUYBOX_V2
	providerFilter: selectedBuyboxProviders,
	// EXP_PROVIDER_BUYBOX_V2
});

const buyboxContainer = ref<HTMLElement | null>(null);

// If the buybox is in the episodes list, use the episode id, objectId, objectType etc. instead of the title data
const buyBoxObjectId = computed(() => (props.episode ? props.episode.objectId : titleObjectId.value));
const buyBoxObjectType = computed(() => (props.episode ? props.episode.objectType : titleObjectType.value));
const buyBoxIsUpcoming = computed(() => (props.episode ? !props.episode.content.isReleased : isTitleUpcoming.value));
const buyBoxSeasonNumber = computed(() => (props.episode ? props.episode.content.seasonNumber : seasonNumber.value));

const { isActive: isInternationalOffersActive, isInternationalOffersTitle } = useInternationalOffersStatus({
	loading,
	title: () => props.title,
});

const buyBoxFilterOptions = computed(() => {
	const allOffers = allRegularOffers.value.concat(dvdBluRayOffers.value);
	return Object.keys(buyboxFilterByMonetizationType).filter(monetizationType => {
		// Only show Disc tab if there are physical offers present
		if (monetizationType === BuyBoxFilterOptions.PHYSICAL) {
			return dvdBluRayOffers.value.length > 0;
		}
		// Only show Buy/Rent tabs if there are non-physical Buy/Rent offers
		const offersToFilter =
			monetizationType === BuyBoxFilterOptions.BUY || monetizationType === BuyBoxFilterOptions.RENT
				? allRegularOffers.value
				: allOffers;

		return offersToFilter.some(offer =>
			buyboxFilterByMonetizationType[monetizationType as keyof typeof buyboxFilterByMonetizationType].includes(
				offer.monetizationType
			)
		);
	}) as BuyBoxFilterOptions[];
});

const titleContext = computed(() => {
	const title = props.title.__typename === 'Season' ? props.title.show : props.title;
	return new SnowplowTitleContextGraphql(
		title.objectId,
		title.objectType,
		buyBoxSeasonNumber.value ?? null,
		props.episodeNumber ?? null
	);
});

provide('titleContext', titleContext);
provide('titleSourceId', buyBoxTitleId.value);
provide('filterOption', buyboxFilter);

// EXP_PROVIDER_BUYBOX_V2
provideLocal(
	PROVIDER_EXP_BB_OFFER_PROVIDERS_KEY,
	computed(() => [...new Set(allRegularOffers.value.map(o => o.package.shortName))])
);
// EXP_PROVIDER_BUYBOX_V2

// EXP_SOFTER_FREE_TRIAL_V2
const { isArbitrating } = useArbitrage();
const activateSoftFreeTrialListV2 = computed(() => !isArbitrating.value && !isInternationalOffersTitle.value);
useActivateExperiment('EXP_SOFTER_FREE_TRIAL_V2', activateSoftFreeTrialListV2);
// EXP_SOFTER_FREE_TRIAL_V2

// EXP_PROMOTED_FREE_TRIAL_REDESIGN
const activatePromotedFreeTrialRedesignExp = computed(
	() => props.title.objectType === ObjectType.Movie && !isArbitrating.value && !isInternationalOffersTitle.value
);
useActivateExperiment('EXP_PROMOTED_FREE_TRIAL_REDESIGN', activatePromotedFreeTrialRedesignExp);
// EXP_PROMOTED_FREE_TRIAL_REDESIGN

// EXP_LINEAR_TV
const { trackEvent } = useExperiment('EXP_LINEAR_TV');
// EXP_LINEAR_TV

// Underperforming Urls Exp

const activateUnderperformingUrlsExp = computed(
	() =>
		inLists.value.includes('EXP_LOWPERF_45') &&
		!props.title.offers.some(offer => offer.package.shortName === 'amp') &&
		props.title.objectType === 'MOVIE'
);

useActivateExperiment('EXP_UNDERPERFORMING_URLS_4', activateUnderperformingUrlsExp);

const lastUpdated = computed(() => {
	if (
		buyboxData.value?.__typename === 'Episode' ||
		buyboxData.value?.__typename === 'Movie' ||
		buyboxData.value?.__typename === 'Season' ||
		buyboxData.value?.__typename === 'Show'
	) {
		return buyboxData.value.maxOfferUpdatedAt;
	}
	return null;
});

const timestampText = computed(() => {
	const updated = timestamp(
		lastUpdated.value,
		{ year: 'numeric', month: 'long', day: 'numeric' },
		country.value,
		language.value
	);
	return t('WEBAPP_BUYBOX_UPDATED_TIMESTAMP', {
		services_total: titleProviderCount.value,
		date: updated.date,
		time: updated.time,
	});
});

async function onNotifyMe() {
	if (props.title && buyBoxTitleId.value) {
		const title = props.title.__typename === 'Season' ? props.title.show : props.title;

		const updatedTitle = await setInWatchlist(
			title.id,
			isInWatchList.value,
			{
				title: title.content.title,
				objectType: title.objectType,
				objectId: title.objectId,
			},
			{
				source: 'buybox',
			}
		);

		if (updatedTitle) {
			emits('onWatchlistToggle', updatedTitle.data?.setInWatchlistV2.title.watchlistEntryV2?.createdAt);
		}
	}
}

function trackInternationalOffersImpression(isVisible: boolean) {
	if (isVisible) {
		trackInternationalOffers('impression');
	}
}

watch(
	isLoggedIn,
	newValue => {
		if (!newValue) return;
		if (props.title && buyBoxTitleId.value) {
			onIsLoggedInChange(
				buyBoxTitleId.value || '',
				{
					title: props.title?.content.title,
					objectType: props.title?.objectType,
					objectId: props.title?.objectId,
				},
				isInWatchList.value,
				'buybox'
			);
		}
	},
	{ immediate: true }
);

async function handleFilterClick(payload: { value: BuyBoxFilterOptions; nonInteraction?: boolean }) {
	const optimisticUpdate = () => {
		if (isEpisodeBuybox.value) {
			episodeBuyBoxTypeOptimistic.value = payload.value;
		} else {
			setBuyboxFilterTypeOptimistic(payload.value);
		}
	};

	const commitUpdate = () => {
		if (isEpisodeBuybox.value) {
			episodeBuyBoxType.value = payload.value;
		} else {
			setNewBuyboxFilterType(payload.value);
		}
	};

	// updating the buybox is expensive.
	// that's why we split it up in 1) optimistic UI update
	optimisticUpdate();
	// then 2) give the JS thread time to render the optimistic UI update immediately
	await new Promise(resolve => setTimeout(resolve, 0));
	// and 3) defer the expensive store commit to the end
	commitUpdate();

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'filter_toggled',
			label: 'buybox',
			property: payload.value,
			nonInteraction: payload.nonInteraction,
		},
		props.additionalContexts
	);

	// EXP_LINEAR_TV
	if (payload.value === BuyBoxFilterOptions.LINEAR_TV) trackEvent({ action: 'click', label: 'livetv_tab' });
	// EXP_LINEAR_TV
}

function watchNowTitleClicked() {
	TrackingHelper.trackEvent(
		'userinteraction',
		{ action: 'click', label: 'watch_now_headline_clicked' },
		props.additionalContexts
	);

	if (!AVODOffer.value) {
		const element = buyboxContainer.value?.querySelector('#regular-buybox');
		if (element) {
			element.scrollIntoView({ behavior: 'smooth' });
		}
	} else {
		ModalHelper.openModal(
			PlexPlayerModal,
			{ offer: AVODOffer.value, title: props.title },
			{ cssClass: 'trailer-modal' },
			'',
			true,
			[]
		);
	}
}

return { __sfc: true,OfferReporter, PlexPlayerModal, BuyboxInternationalOffers, emits, props, setInWatchlist, onIsLoggedInChange, userStore, setNewBuyboxFilterType, setBuyboxFilterTypeOptimistic, newBuyboxFilterType, buyboxFilterTypeOptimistic, isLoggedIn, country, language, titleProviderCount, inLists, t, trackInternationalOffers, titleId, titleName, titleObjectId, titleObjectType, isTitleUpcoming, seasonNumber, isInWatchList, mergeBuyRentExpVariant, linearTVVariant, isLinearTvExperiment, buyBoxTitleId, queryVariables, result, loading, linearEpisodesQueryVariables, linearEpisodesResult, expandBuyRentOffers, expandedQueryVariables, expandedResult, buyboxData, isEpisodeBuybox, episodeBuyBoxType, episodeBuyBoxTypeOptimistic, buyboxFilterOptimistic, buyboxFilter, buyboxUpcomingReleases, selectedBuyboxProviders, showSavePrompt, onSaveServicesSignUp, SaveServicesPrompt, filteredRegularOffers, allRegularOffers, upcomingReleases, upcomingReleasesFromGroups, promotedOffers, AVODOffer, dvdBluRayOffers, hasVODOffers, buyboxContainer, buyBoxObjectId, buyBoxObjectType, buyBoxIsUpcoming, buyBoxSeasonNumber, isInternationalOffersActive, isInternationalOffersTitle, buyBoxFilterOptions, titleContext, isArbitrating, activateSoftFreeTrialListV2, activatePromotedFreeTrialRedesignExp, trackEvent, activateUnderperformingUrlsExp, lastUpdated, timestampText, onNotifyMe, trackInternationalOffersImpression, handleFilterClick, watchNowTitleClicked, mrcObservableOptions, BuyBoxCountrySwitcher, BuyBoxTypeSwitcher, ListBuyBoxFilter, ListUpcomingNoOffers, PromotedListBuyBox, RegularListBuyBox }
}

})